import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import { logger } from "../../utils/logger";
import useSharedWebsocket from "../../hooks/wss/useSharedWebsocket";
import { parseSocketPayload } from "../../hooks/wss/parseFrame";
import { AuthContext } from "../AuthContext";
import { clearStoredApiCredentials } from "../AuthContext/helpers";
import { jsonStringify } from "../../utils/strings";
import {
  IWebsocketContextType,
  IWebsocketContextProviderProps,
  IAuthReplyData,
} from "./types";
import {
  clampedBackoffMs,
  createSubscriptionManager,
  IWsServerAck,
  IWsServerError,
  SubscriptionManager,
} from "./subscriptionManager";

export type { ISubscribeIDMap } from "./types";

export const WebsocketContext = React.createContext<IWebsocketContextType>({
  authenticated: false,
  triggerSubscribe: () => {},
  triggerUnsubscribe: () => {},
  registerRevalidate: () => () => {},
  lastMessages: null,
  connectionStatus: "connecting",
});

/** The auth reply is the only `message` event carrying `data.success`. */
export const parseAuthReply = (message: unknown): IAuthReplyData | undefined => {
  // The transport already decodes frames; a raw string is still accepted.
  const parsed: any = parseSocketPayload(message, { reportInvalid: false });
  const data = parsed?.data;
  if (!data || typeof data !== "object" || typeof data.success !== "boolean") {
    return undefined;
  }
  return data as IAuthReplyData;
};

const breadcrumb = (message: string, data?: Record<string, unknown>) =>
  Sentry.addBreadcrumb?.({ category: "websocket", message, data, level: "warning" });

export function WebsocketContextProvider({
  children,
}: IWebsocketContextProviderProps) {
  const { apiKey, apiSecret, account, deleteAuthInfo } =
    useContext(AuthContext);
  const accountKey = account ? account.toLowerCase() : undefined;

  /**
   * The account the socket is authenticated as — not a boolean (FE-3). A late
   * success for wallet A can never mark wallet B authenticated, and every
   * change of account re-renders so private hooks re-subscribe.
   */
  const [authenticatedAccount, setAuthenticatedAccount] = useState<string>();
  const authenticated =
    accountKey !== undefined && authenticatedAccount === accountKey;

  // Read from socket event handlers, which outlive any one render.
  const latest = useRef({ accountKey, apiKey, apiSecret, deleteAuthInfo });
  latest.current = { accountKey, apiKey, apiSecret, deleteAuthInfo };

  const sendRef = useRef<(message: string) => void>(() => {});
  const managerRef = useRef<SubscriptionManager>();
  if (!managerRef.current) {
    managerRef.current = createSubscriptionManager({
      send: (op, channel) =>
        sendRef.current(jsonStringify({ op, data: [channel] })),
      report: breadcrumb,
    });
  }
  const manager = managerRef.current;

  // Accounts of auth requests awaiting a reply, oldest first. Replies come
  // back in request order on one socket, so this tells which wallet a reply
  // without an `account` belongs to.
  const pendingAuth = useRef<string[]>([]);
  /**
   * The identity the SERVER holds for this connection, from its own replies:
   * set by any success, cleared by any failure (the server drops the identity
   * on every failed attempt) and by a new connection. Unlike the local
   * session, a wallet switch does not clear it until the server says so.
   */
  const serverIdentity = useRef<string>();
  const authRetry = useRef<{
    timer?: ReturnType<typeof setTimeout>;
    attempts: number;
  }>({ attempts: 0 });

  const clearAuthRetry = useCallback((resetAttempts: boolean) => {
    if (authRetry.current.timer !== undefined) {
      clearTimeout(authRetry.current.timer);
      authRetry.current.timer = undefined;
    }
    if (resetAttempts) authRetry.current.attempts = 0;
  }, []);

  const sendAuth = useCallback(() => {
    const { accountKey: current, apiKey: key, apiSecret: secret } =
      latest.current;
    if (!manager.isConnected() || !current || !key || !secret) return;

    logger.debug("WS open and api key available. Attempting auth...");
    pendingAuth.current.push(current);
    sendRef.current(
      JSON.stringify({ op: "auth", data: { key, secret } })
    );
  }, [manager]);

  const deauthenticate = useCallback(() => {
    manager.onDeauthenticated();
    setAuthenticatedAccount(undefined);
  }, [manager]);

  const handleAuthReply = useCallback(
    (reply: IAuthReplyData) => {
      const { accountKey: current } = latest.current;
      const replyAccount = reply.account ? reply.account.toLowerCase() : undefined;

      // The server sends AUTH_EXPIRED only on its own, when the key of the
      // identity it holds reaches its TTL — never as the answer to an auth
      // request. So it must not consume (and be attributed to) a pending
      // request, e.g. the one just sent for a newly connected wallet (L1).
      const isExpiryPush =
        !reply.success && reply.code === "AUTH_EXPIRED" && replyAccount === undefined;
      const requested = isExpiryPush ? undefined : pendingAuth.current.shift();

      if (reply.success) {
        serverIdentity.current = replyAccount;
        if (!current || replyAccount !== current) {
          logger.debug("Ignoring auth success for another account", replyAccount);
          return;
        }
        clearAuthRetry(true);
        manager.onAuthenticated(current);
        setAuthenticatedAccount(current);
        return;
      }

      // A failure names its account; else an expiry push belongs to the
      // identity the server held; else the reply answers the oldest request.
      const target = isExpiryPush
        ? serverIdentity.current
        : replyAccount ?? requested ?? manager.getAuthenticatedAccount();
      serverIdentity.current = undefined;
      if (!current || target !== current) {
        logger.debug("Ignoring auth failure for another account", target);
        return;
      }

      deauthenticate();
      switch (reply.code) {
        case "AUTH_INVALID":
          // The server does not know this key: it can never work again.
          clearAuthRetry(true);
          latest.current.deleteAuthInfo("api");
          return;
        case "AUTH_EXPIRED":
          // The key reached its TTL (H2). Clear the API key, secret and signing
          // key together: the app asks the trader to enable trading again, and
          // with no stored key that registration mints a NEW API key. The
          // changed credentials re-run the auth effect below, which
          // authenticates the socket, and the private channels replay.
          clearAuthRetry(true);
          latest.current.deleteAuthInfo("trading");
          return;
        default: {
          // AUTH_UNAVAILABLE, AUTH_RATE_LIMITED, or a server that sends no
          // code (e.g. its Redis was down): transient, keep the credentials.
          clearAuthRetry(false);
          const delay = clampedBackoffMs(
            authRetry.current.attempts,
            reply.retryAfterMs
          );
          authRetry.current.attempts += 1;
          authRetry.current.timer = setTimeout(() => {
            authRetry.current.timer = undefined;
            sendAuth();
          }, delay);
        }
      }
    },
    [clearAuthRetry, deauthenticate, manager, sendAuth]
  );

  const { sendMessage, lastMessages, connectionStatus, reconnect } =
    useSharedWebsocket({
      onConnect: () => {
        pendingAuth.current = [];
        serverIdentity.current = undefined;
        clearAuthRetry(true);
        manager.onConnect();
        setAuthenticatedAccount(undefined);
        sendAuth();
      },
      onDisconnect: () => {
        pendingAuth.current = [];
        serverIdentity.current = undefined;
        clearAuthRetry(true);
        manager.onDisconnect();
        setAuthenticatedAccount(undefined);
      },
      onMessage: (message) => {
        const reply = parseAuthReply(message);
        if (reply) handleAuthReply(reply);
      },
      onServerError: (error?: IWsServerError) => {
        const disposition = manager.handleServerError(error);
        // Superseded and withdrawn requests (market switches, StrictMode
        // remounts) are answered by design and ignored; logging each one only
        // floods the console. Every other error is still logged.
        if (disposition === "ignored" && error?.code === "REQUEST_CANCELLED") return;
        logger.debug("WS server error", error, disposition);
      },
      onServerAck: (ack: IWsServerAck | undefined, event) => {
        if (event === "subscribed") manager.handleServerAck(ack);
      },
      onFramesDropped: (channels) => {
        channels.forEach((channel) => manager.revalidate(channel));
      },
    });
  sendRef.current = sendMessage;

  // Account or credentials changed: drop the old identity and authenticate again.
  const previousAccountKey = useRef(accountKey);
  useEffect(() => {
    const previous = previousAccountKey.current;
    previousAccountKey.current = accountKey;
    clearAuthRetry(true);

    if (previous !== accountKey) {
      deauthenticate();
      if (previous && !accountKey) {
        // Wallet disconnected: forget its stored credentials and give the
        // socket a fresh, unauthenticated session (SEC-5).
        clearStoredApiCredentials(previous);
        reconnect();
        return;
      }
    }
    sendAuth();
  }, [
    accountKey,
    apiKey,
    apiSecret,
    clearAuthRetry,
    deauthenticate,
    reconnect,
    sendAuth,
  ]);

  useEffect(
    () => () => {
      clearAuthRetry(true);
      manager.dispose();
    },
    [clearAuthRetry, manager]
  );

  /**
   * Compatibility layer over the subscription manager. Each channel is
   * reference counted; stable identities so consumers' effects never re-run
   * just because the socket or auth state changed. The third argument
   * (`autoUnsubToChannelString`) is ignored — release in effect cleanup.
   */
  const triggerSubscribe = useCallback(
    (op: string, data: string[]) => {
      data.forEach((channel) =>
        op === "unsubscribe"
          ? manager.release(channel)
          : manager.acquire(channel)
      );
    },
    [manager]
  );

  const triggerUnsubscribe = useCallback(
    (data: string[]) => {
      data.forEach((channel) => manager.release(channel));
    },
    [manager]
  );

  const registerRevalidate = useCallback(
    (data: string[], revalidate: () => void) => {
      const unregisters = data.map((channel) =>
        manager.registerRevalidate(channel, revalidate)
      );
      return () => unregisters.forEach((unregister) => unregister());
    },
    [manager]
  );

  // A stable value: consumers re-render only when one of these changes, not on
  // every render of the provider (FE-12).
  const value = useMemo<IWebsocketContextType>(
    () => ({
      authenticated,
      triggerSubscribe,
      triggerUnsubscribe,
      registerRevalidate,
      lastMessages,
      connectionStatus,
    }),
    [
      authenticated,
      triggerSubscribe,
      triggerUnsubscribe,
      registerRevalidate,
      lastMessages,
      connectionStatus,
    ]
  );

  return (
    <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>
  );
}
